import React, { useState, useRef } from 'react';
import './cro-sprint.css';
import './monthly-cro-retainer.css';

import useScrollReveal from '../hooks/useScrollReveal';
import usePageMeta from '../hooks/usePageMeta';

import SprintHero from '../components/cro/sprint-hero';
import SprintTextSection from '../components/cro/sprint-text-section';
import SprintFeatureGrid from '../components/cro/sprint-feature-grid';
import SprintFlexCards from '../components/cro/sprint-flex-cards';
import SprintPillCards from '../components/cro/sprint-pill-cards';
import SprintOutcomes from '../components/cro/sprint-outcomes';
import SprintCta from '../components/cro/sprint-cta';

import {
    IconRefreshCw,
    IconSearch,
    IconBarChart,
    IconTarget,
    IconFlask,
    IconTrendingUp,
    IconZap,
    IconEye,
    IconLayers,
    IconCalendar,
    IconFileText,
    IconSmartphone,
    IconShoppingCart,
    IconUsers,
    IconDollarSign,
    IconActivity,
    IconClipboard,
    IconMousePointer,
    IconShoppingBag,
    IconBox,
    IconGlobe,
    IconMonitor,
    IconRepeat,
    IconCreditCard,
    IconMegaphone,
    IconCheckCircleW,
    IconArrowUpW
} from '../components/sprint-icons';

export default function MonthlyCroRetainer() {
    const pageRef = useRef(null);
    const [activeStep, setActiveStep] = useState(0);

    useScrollReveal(pageRef);
    usePageMeta(
        'Monthly CRO Retainer & A/B Testing | Spinnennetz',
        'Ensure continuous growth with our monthly CRO retainer. Ongoing A/B testing, analysis, and optimization to consistently improve conversion rates and revenue.',
        '/monthly-cro-retainer',
        { "@context": "https://schema.org", "@type": "Service", "name": "Monthly CRO Retainer & A/B Testing", "provider": { "@type": "Organization", "name": "Spinnennetz" } }
    );

    const whatsIncludedItems = [
        { icon: <IconFlask />, title: 'Continuous Experimentation', desc: 'We relentlessly develop, launch, and analyze A/B tests to pinpoint winning experiences and systematically elevate conversions across every touchpoint.' },
        { icon: <IconActivity />, title: 'Performance Monitoring', desc: 'Track mission-critical metrics across your entire funnel in real-time, uncovering hidden opportunities before they erode your revenue potential.' },
        { icon: <IconDollarSign />, title: 'Revenue Optimization', desc: 'Amplify average order value, conversion rates, checkout performance, and customer acquisition efficiency through precision-engineered strategies.' },
        { icon: <IconMousePointer />, title: 'User Behavior Analysis', desc: 'Leverage advanced analytics, heatmaps, session recordings, and deep customer insights to architect optimization efforts grounded in real data.' },
        { icon: <IconClipboard />, title: 'Conversion Opportunity Pipeline', desc: 'Maintain a meticulously prioritized backlog of high-impact optimization ideas and experiments, ensuring your growth engine never stalls.' },
        { icon: <IconCalendar />, title: 'Monthly Strategy Reviews', desc: 'Comprehensive performance reviews, strategic insights, and aligned optimization efforts that keep every initiative laser-focused on your business objectives.' },
        { icon: <IconFileText />, title: 'Executive Reporting', desc: 'Crystal-clear, actionable reports focused on tangible business impact, key learnings, and decisive next steps for sustained competitive advantage.' },
    ];

    const processSteps = [
        {
            num: 1,
            icon: <IconSearch />,
            title: 'Analyze',
            badge: 'Phase 01: Auditing & Insights',
            desc: 'Deep-dive into analytics, customer behavior patterns, and conversion data to uncover transformative opportunities hiding in plain sight.',
            extendedDesc: 'Our optimization begins with a comprehensive, data-driven diagnostic. We analyze quantitative metrics, funnel friction points, and user session behaviors to diagnose exactly where and why users drop off, turning raw analytics into high-yield optimization opportunities.',
            highlights: [
                'Quantitative funnel drop-off audit',
                'Qualitative heatmap & session recording analysis',
                'Form & checkout friction analysis',
                'Advanced user behavior segmentation analytics'
            ]
        },
        {
            num: 2,
            icon: <IconLayers />,
            title: 'Prioritize',
            badge: 'Phase 02: Strategic Backlog',
            desc: 'Strategically select the highest-impact initiatives based on potential revenue gains, implementation effort, and projected return on investment.',
            extendedDesc: 'Not all ideas are created equal. We score and map every test hypothesis using the globally recognized ICE framework (Impact, Confidence, Ease). This ensures we invest resources exclusively into testing initiatives that yield the maximum possible revenue lift in the shortest timeframe.',
            highlights: [
                'ICE framework hypothesis scoring',
                'High-impact vs. low-effort roadmap mapping',
                'Conversion roadmap & timeline alignment',
                'Resource & development requirement audits'
            ]
        },
        {
            num: 3,
            icon: <IconFlask />,
            title: 'Experiment',
            badge: 'Phase 03: Precision Execution',
            desc: 'Launch meticulously crafted A/B tests and optimization initiatives across key pages and conversion funnels to validate hypotheses with precision.',
            extendedDesc: 'We build, launch, and execute clean, statistically rigorous experiments. From high-converting layout redesigns and persuasive copywriting edits to friction-free checkout variations, our engineers and designers construct A/B tests that run flawlessly without affecting site performance.',
            highlights: [
                'A/B and multivariate experiment configuration',
                'High-fidelity UX design & frontend development',
                'Frictionless checkout path experimentation',
                'Targeted audience & behavioral triggers'
            ]
        },
        {
            num: 4,
            icon: <IconBarChart />,
            title: 'Measure',
            badge: 'Phase 04: Analytics Validation',
            desc: 'Rigorously evaluate results, document invaluable learnings, and validate winning variations with statistical confidence before scaling.',
            extendedDesc: 'We verify results with rigorous, multi-point statistical validation. We track micro-conversions, primary conversions, and bottom-line revenue impact to guarantee that every winning variation represents a real, statistically confident step forward for your business.',
            highlights: [
                'Statistical significance checks (95%+ confidence)',
                'Revenue & average order value impact reviews',
                'Secondary metric performance audits',
                'Comprehensive post-test behavior analysis'
            ]
        },
        {
            num: 5,
            icon: <IconTrendingUp />,
            title: 'Scale',
            badge: 'Phase 05: Compounding Lift',
            desc: 'Implement proven, successful changes and leverage accumulated insights to fuel exponential, compounding growth across your digital ecosystem.',
            extendedDesc: 'When an experiment wins, we permanently deploy the optimized design to all users. The insights we gather are then documented and fed back into the diagnostic engine, creating a self-reinforcing loop of optimization that builds compounding growth month over month.',
            highlights: [
                '100% rollout execution of winning designs',
                'Integration of insights into secondary pages',
                'Continuous roadmap optimization loop',
                'Centralized experiment knowledge base update'
            ]
        }
    ];

    const areasWeOptimize = [
        { icon: <IconTarget />, title: 'Landing Pages', desc: 'Dramatically increase conversion rates through refined messaging, elevated design, and frictionless user experiences that captivate visitors.' },
        { icon: <IconShoppingCart />, title: 'Product Pages', desc: 'Enhance product presentation, amplify trust signals, and ignite purchase intent with compelling layouts that turn browsers into buyers.' },
        { icon: <IconSmartphone />, title: 'Mobile Experience', desc: 'Eliminate friction and supercharge engagement across mobile devices, capturing the ever-growing segment of mobile-first shoppers.' },
        { icon: <IconShoppingCart />, title: 'Checkout Flow', desc: 'Optimize every step of the path to purchase and dramatically reduce abandonment with streamlined, confidence-inspiring checkout experiences.' },
        { icon: <IconUsers />, title: 'Lead Generation Funnels', desc: 'Maximize form submissions and elevate lead quality through strategically designed capture points and persuasive conversion pathways.' },
        { icon: <IconEye />, title: 'Customer Journey', desc: 'Identify and systematically dismantle obstacles throughout the entire conversion process, crafting seamless end-to-end experiences.' },
    ];

    const monthlyDeliverables = [
        { icon: <IconCheckCircleW />, label: 'CRO Strategy & Roadmap' },
        { icon: <IconCheckCircleW />, label: 'Experiment Planning' },
        { icon: <IconCheckCircleW />, label: 'A/B Test Management' },
        { icon: <IconCheckCircleW />, label: 'Analytics Reviews' },
        { icon: <IconCheckCircleW />, label: 'User Behavior Analysis' },
        { icon: <IconCheckCircleW />, label: 'Monthly Performance Reports' },
        { icon: <IconCheckCircleW />, label: 'Revenue Impact Tracking' },
        { icon: <IconCheckCircleW />, label: 'Strategic Recommendations' },
        { icon: <IconCheckCircleW />, label: 'Dedicated CRO Support' },
    ];

    const whyPartnerItems = [
        { icon: <IconDollarSign />, title: 'Focused on Revenue Growth', desc: 'We measure success by tangible business outcomes and bottom-line impact — never by vanity metrics or superficial indicators.' },
        { icon: <IconBarChart />, title: 'Data-Driven Optimization', desc: 'Every single recommendation is meticulously grounded in customer behavior analytics, rigorous testing results, and proven methodologies.' },
        { icon: <IconUsers />, title: 'Dedicated Growth Partner', desc: 'Gain uninterrupted access to elite CRO specialists without the overhead of hiring, training, and maintaining an internal optimization team.' },
        { icon: <IconTrendingUp />, title: 'Long-Term Performance', desc: 'Build an unstoppable culture of experimentation that compounds growth over time, creating sustainable competitive advantages that endure.' },
    ];

    const idealFor = [
        { icon: <IconShoppingBag />, label: 'Shopify Stores' },
        { icon: <IconBox />, label: 'D2C Brands' },
        { icon: <IconGlobe />, label: 'eCommerce Businesses' },
        { icon: <IconRepeat />, label: 'Subscription Brands' },
        { icon: <IconMonitor />, label: 'SaaS Companies' },
        { icon: <IconCreditCard />, label: 'Lead Generation Businesses' },
        { icon: <IconMegaphone />, label: 'Brands Investing in Paid Advertising' },
    ];

    const expectedResults = [
        { icon: <IconCheckCircleW />, label: 'Higher Conversion Rates' },
        { icon: <IconArrowUpW />, label: 'Increased Revenue Per Visitor' },
        { icon: <IconCheckCircleW />, label: 'Improved Customer Experience' },
        { icon: <IconArrowUpW />, label: 'More Efficient Marketing Spend' },
        { icon: <IconCheckCircleW />, label: 'Higher Average Order Value' },
        { icon: <IconArrowUpW />, label: 'Reduced Funnel Drop-Offs' },
        { icon: <IconCheckCircleW />, label: 'Sustainable Revenue Growth' },
    ];

    const heroMetrics = [
        { icon: <IconRefreshCw />, value: '∞', label: 'Continuous Testing' },
        { icon: <IconTrendingUp />, value: '+67%', label: 'Revenue Growth' },
        { icon: <IconBarChart />, value: '3.2×', label: 'ROI Multiplier' },
        { icon: <IconZap />, value: '24/7', label: 'Growth Engine' },
    ];

    const currentStep = processSteps[activeStep] || processSteps[0];

    return (
        <div className="cro-sprint-page retainer-page" ref={pageRef}>
            <SprintHero
                badgeText="Monthly CRO Retainer"
                titlePrefix="Continuous Optimization."
                highlightText="Sustainable Growth."
                titleSuffix=""
                subtitle="Launching a website or running a few A/B tests is only the beginning. The highest-performing brands treat conversion rate optimization as an ongoing, relentless process — not a one-time project."
                subtitle2="Our Monthly CRO Retainer provides continuous experimentation, performance analysis, and revenue optimization to help your business consistently improve conversion rates and maximize the value of every single visitor."
                primaryButtonText="Schedule a Growth Consultation"
                secondaryButtonText="Explore What's Included"
                secondaryHref="#whats-included"
                metrics={heroMetrics}
                extraClasses="retainer-hero"
            />

            <SprintTextSection
                label="Why Ongoing CRO Matters"
                titlePrefix="The Market Never Stops Evolving."
                highlightText="Neither Should Your Optimization."
                titleSuffix=""
                paragraphs={[
                    'Customer behavior shifts. Traffic sources evolve. Competitors relentlessly improve. What delivered exceptional results six months ago may be silently undermining your performance today.',
                    'Continuous optimization ensures your website perpetually adapts, systematically improves, and consistently outperforms — keeping you ahead of changing customer expectations and volatile market conditions.',
                    'We become a seamless extension of your team, identifying untapped opportunities, running precision experiments, and delivering measurable business growth month after month.'
                ]}
                extraClasses="retainer-why-matters"
            />

            <SprintFeatureGrid
                sectionId="whats-included"
                label="What's Included"
                titlePrefix="A Comprehensive Suite of"
                highlightText="Growth-Driving Services"
                titleSuffix=""
                subtitle="Every facet of your conversion ecosystem, meticulously monitored, optimized, and elevated by our dedicated team of specialists."
                items={whatsIncludedItems}
            />

            {/* Custom Process Section for Monthly Retainer */}
            <section className="sprint-process retainer-process">
                <div className="container">
                    <div className="sprint-section-header sprint-reveal">
                        <span className="sprint-section-label center">Our Monthly CRO Process</span>
                        <h2 className="sprint-section-title">
                            A Proven Five-Phase Framework for <span className="highlight">Compounding Growth</span>
                        </h2>
                        <p className="sprint-section-subtitle" style={{ margin: '0 auto' }}>
                            Each month, we execute a disciplined cycle designed to identify, test, validate, and scale the optimizations that move your revenue needle.
                        </p>
                    </div>

                    <div className="process-dashboard sprint-reveal">
                        <div className="process-tabs-row">
                            {processSteps.map((step, idx) => (
                                <button
                                    key={step.title}
                                    className={`process-tab-btn ${activeStep === idx ? 'active' : ''}`}
                                    onClick={() => setActiveStep(idx)}
                                >
                                    <div className="tab-btn-num">{`0${step.num}`}</div>
                                    <div className="tab-btn-content">
                                        <div className="tab-btn-icon">{step.icon}</div>
                                        <span className="tab-btn-title">{step.title}</span>
                                    </div>
                                </button>
                            ))}
                        </div>

                        <div className="process-detail-card">
                            <div className="process-detail-grid">
                                <div className="process-detail-left">
                                    <div className="process-detail-badge-row">
                                        <span className="process-detail-badge">{currentStep.badge}</span>
                                    </div>
                                    <h3 className="process-detail-title">{currentStep.title}</h3>
                                    <p className="process-detail-desc">{currentStep.extendedDesc}</p>
                                </div>
                                <div className="process-detail-right">
                                    <h4 className="highlights-title">Key Activities & Focus Areas:</h4>
                                    <ul className="process-highlights-list">
                                        {currentStep.highlights.map((highlight, hIdx) => (
                                            <li key={hIdx}>
                                                <div className="highlight-check-wrap">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                </div>
                                                <span>{highlight}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="process-mobile-accordion">
                        {processSteps.map((step, idx) => {
                            const isOpen = activeStep === idx;
                            return (
                                <div key={step.title} className={`process-accordion-item ${isOpen ? 'open' : ''}`}>
                                    <button
                                        className="process-accordion-header"
                                        onClick={() => setActiveStep(isOpen ? -1 : idx)}
                                    >
                                        <div className="accordion-header-left">
                                            <span className="accordion-num">{`0${step.num}`}</span>
                                            <div className="accordion-icon">{step.icon}</div>
                                            <span className="accordion-title">{step.title}</span>
                                        </div>
                                        <div className="accordion-arrow">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                        </div>
                                    </button>
                                    <div className="process-accordion-collapse">
                                        <div className="process-accordion-body">
                                            <span className="process-detail-badge">{step.badge}</span>
                                            <p className="process-accordion-desc">{step.extendedDesc}</p>
                                            <h5 className="highlights-title">Key Activities:</h5>
                                            <ul className="process-highlights-list">
                                                {step.highlights.map((highlight, hIdx) => (
                                                    <li key={hIdx}>
                                                        <div className="highlight-check-wrap">
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                        </div>
                                                        <span>{highlight}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <SprintFlexCards
                label="Areas We Optimize"
                titlePrefix="Every Critical Touchpoint,"
                highlightText="Precision-Engineered"
                titleSuffix=""
                subtitle="From first impression to final conversion, we optimize every stage of the customer journey to extract maximum value from your existing traffic."
                items={areasWeOptimize}
                accentVariant="left"
                sectionClass="retainer-areas"
                gridClass="retainer-areas-grid"
            />

            <SprintPillCards
                label="Monthly Deliverables"
                titlePrefix="What You'll Receive"
                highlightText="Every Month"
                titleSuffix=""
                items={monthlyDeliverables}
                hoverDirection="up"
                sectionClass="retainer-deliverables"
            />

            <SprintFlexCards
                label="Why Partner With Us?"
                titlePrefix="The Competitive Edge Your Brand"
                highlightText="Deserves"
                titleSuffix=""
                items={whyPartnerItems}
                accentVariant="bottom"
            />

            <SprintPillCards
                label="Ideal For"
                titlePrefix="Engineered for Ambitious Brands"
                highlightText="Ready to Dominate"
                titleSuffix=""
                items={idealFor}
                hoverDirection="right"
            />

            <SprintOutcomes
                title="Expected Results"
                items={expectedResults}
                colsClass="cols-4"
            />

            <SprintCta
                title="Turn Data Into Revenue"
                subtitle="The most successful brands don't rely on assumptions — they continuously test, learn, and optimize. Partner with us to create a systematic CRO program that drives measurable growth month after month."
                subtitle2="Stop leaving revenue on the table. Start building a conversion engine that compounds."
                buttonText="Schedule a Growth Consultation"
                extraClasses="retainer-cta"
            />
        </div>
    );
}
